// Fenêtre « Hall of Fame », sur le modèle du bloc « Meilleurs joueurs » d'apps.0cog.com (zerocog/apps, public/leaderboard.js) :
// trois onglets (semaine, mois, tous les temps), une ligne arrondie par joueur (rang ou médaille, avatar, pseudo, ville et
// drapeau, score), les plus anciens grisés et datés, et la date de mise à jour. La ligne du joueur est mise en évidence ; s'il
// est plus bas que les lignes montrées, elle s'ajoute en dessous.
import { $, show, hide, setTextWithEmoji } from './dom.js';
import { LIST_KEYS, boards, flagOf } from '../core/leaderboard.js';

const MEDALS = ['🥇', '🥈', '🥉'];

/** @param {{ t: (key: string, params?: object) => string, getLanguage: () => string }} i18n */
export function createLeaderboardView({ t, getLanguage }) {
  const modal = $('leaderboardModal');
  const tabs = $('hofTabs');
  const list = $('globalLeaderboardList');
  const empty = $('hofEmpty');
  const meta = $('hofMeta');
  let selected = 'week';
  let last = null; // { summary, myId } : de quoi redessiner au changement d'onglet ou de langue

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) setTextWithEmoji(node, text);
    return node;
  };

  function row({ entry, rank, fill }, myId, locale) {
    const podium = !fill && rank <= 3;
    const classes = ['hof-row', fill ? 'is-fill' : podium ? 'is-podium' : '', entry.id === myId ? 'is-me' : ''];
    const li = el('li', classes.filter(Boolean).join(' '));
    li.appendChild(el('span', 'hof-rank', podium ? MEDALS[rank - 1] : String(rank)));
    li.appendChild(el('span', 'hof-emoji', entry.emoji || '🙂'));
    const who = el('span', 'hof-who');
    who.appendChild(el('span', 'hof-nickname', entry.nickname));
    const placeText = [entry.city, flagOf(entry.countryCode)].filter(Boolean).join(' ');
    const date = fill && entry.date ? new Date(entry.date).toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : '';
    const line = [placeText && `📍 ${placeText}`, date].filter(Boolean).join(' · ');
    if (line) who.appendChild(el('span', 'hof-place', line));
    li.appendChild(who);
    const points = el('span', 'hof-points', new Intl.NumberFormat(locale).format(entry.score));
    points.appendChild(el('span', 'hof-unit', ` ${t('scoreboard.points')}`));
    li.appendChild(points);
    return li;
  }

  function renderTabs() {
    tabs.replaceChildren(...LIST_KEYS.map((key) => {
      const b = el('button', 'hof-tab', t(`leaderboard.tab.${key}`));
      b.type = 'button';
      b.dataset.list = key;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(key === selected));
      b.tabIndex = key === selected ? 0 : -1;
      return b;
    }));
  }

  /**
   * @param {{updated: string|null, all: object[], month: object[], week: object[]}} summary
   * @param {string|null} myId identifiant du joueur dans ce Hall of Fame
   */
  function render(summary, myId = null) {
    last = { summary, myId };
    const locale = getLanguage();
    renderTabs();
    const { rows, me } = boards(summary, myId, Date.now())[selected];
    const items = rows.map((r) => row(r, myId, locale));
    if (me) items.push(el('li', 'hof-gap', '⋯'), row(me, myId, locale));
    list.replaceChildren(...items);
    empty.hidden = rows.length > 0;
    empty.textContent = rows.length > 0 ? '' : t('leaderboard.empty');
    const updated = summary.updated ? new Date(summary.updated) : null;
    meta.textContent = updated && !Number.isNaN(updated.getTime())
      ? t('leaderboard.updated', { date: updated.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) })
      : '';
  }

  function select(key, focus = false) {
    selected = key;
    if (last) render(last.summary, last.myId);
    if (focus) tabs.querySelector(`[data-list="${key}"]`)?.focus();
  }

  tabs.addEventListener('click', (e) => {
    const b = e.target.closest('[data-list]');
    if (b) select(b.dataset.list);
  });
  tabs.addEventListener('keydown', (e) => {
    const i = LIST_KEYS.indexOf(selected);
    const to = { ArrowRight: i + 1, ArrowLeft: i - 1 + LIST_KEYS.length, Home: 0, End: LIST_KEYS.length - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    select(LIST_KEYS[to % LIST_KEYS.length], true);
  });

  return {
    render,
    /** Ouvre sur l'onglet de la semaine. */
    open: () => { select('week'); show(modal); },
    close: () => hide(modal),
    btnOpen: $('btnShowLeaderboard'),
    btnClose: $('btnCloseLeaderboard'),
  };
}
